import { each, get, mapValues } from "lodash";
import isEmpty from "lodash/isEmpty";
import validate from "validate.js";
import listingValidationNonRequired from "../../listing/ducks/listingValidationNonRequired";
import listingValidation from "../../listing/ducks/validation";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

export const outsideOrAppraisalCompConstraintsNotRequired = {
    Industrial: {
        "listing.attributes.sfAvail": {
            ...constraints.number,
        },
        "listing.attributes.largestContigSf": {
            ...constraints.number,
        },
        "listing.attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.coldStorageSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "listing.attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "listing.attributes.blendedRate": {
            ...constraints.number,
        },
        "listing.attributes.ampsNum": {
            ...constraints.number,
        },
        "listing.attributes.phaseNum": {
            ...constraints.number,
        },
        "listing.attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    Hospitality: {
        "listing.attributes.hospitalitySf": {
            ...constraints.id,
        },
        "listing.attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "listing.attributes.roomCount": {
            ...constraints.id,
        },
    },
    Educational: {
        "listing.attributes.classroomCount": {
            ...constraints.number,
        },
        "listing.attributes.auditoriumSeatsCount": {
            ...constraints.number,
        },
    },
    totalConsideration: {
        commencementDate: {
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.date,
        },
        termNumMonths: {
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.number,
        },
        ratePerMonth: {
            ...constraints.number,
        },
        ratePerSqftPerMonth: {
            ...constraints.number,
        },
        ratePerSqftPerYear: {
            ...constraints.number,
        },
        escalationRate: {
            ...constraints.number,
        },
        escalationTerm: {
            ...constraints.number,
        },
    },
    listingInformation: {
        "listing.dateAvailable": {
            ...constraints.date,
        },
        "listing.expirationDate": {
            ...constraints.date,
        },
        "listing.leaseExpirationDate": {
            ...constraints.date,
        },
        "listing.attributes.numBldg": {
            ...constraints.number,
        },
        "listing.attributes.lotAcres": {
            ...constraints.number,
        },
        "listing.attributes.lotSf": {
            ...constraints.number,
        },
        "listing.sfAvail": {
            ...constraints.number,
        },
        "listing.sfDivisibleTo": {
            ...constraints.number,
        },
    },
    leaseDetails: {
        tiRate: {
            ...constraints.number,
            ...constraints.between0And1000,
        },
        movingAllowanceAmount: {
            ...constraints.number,
        },
        naicsCode: {
            ...constraints.id,
        },
        sicCode: {
            ...constraints.id,
        },
        currentSf: {
            ...constraints.number,
        },
        squareFeetChange: {
            ...constraints.number,
        },
    },
    Lease: {
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.date,
        },
    },
    Sale: {
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.date,
        },
        squareFeet: {
            ...constraints.number,
        },
        closedDate: {
            ...constraints.date,
        },
    },
    parkingInformation: {
        "listing.attributes.parkingTotal": {
            ...constraints.number,
        },
        "listing.attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
    },
    listingFinancial: {
        "listing.improvementAllowance": {
            ...constraints.number,
        },
        "listing.askingRentMax": {
            ...constraints.number,
        },
        "listing.askingRentPerMonth": {
            ...constraints.number,
        },
        "listing.baseRentPerYear": {
            ...constraints.number,
        },
        "listing.nnnPerMonth": {
            ...constraints.number,
        },
        "listing.nnnPerYear": {
            ...constraints.number,
        },
        "listing.totalMonthlyRent": {
            ...constraints.number,
        },
        "listing.totalRentPerYear": {
            ...constraints.number,
        },
    },
};

const validateNonRequired = (comp, useType, transactionType, compType) => {
    let whatToValidate = [];
    switch (transactionType) {
        case "Lease":
            whatToValidate = [
                useType,
                transactionType,
                "leaseDetails",
                "totalConsideration",
                "listingInformation",
                "parkingInformation",
                "listingFinancial",
            ];
            break;
        case "Sale":
            whatToValidate = [useType, transactionType, "listingInformation", "parkingInformation"];
            break;
        default:
            break;
    }
    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...outsideOrAppraisalCompConstraintsNotRequired[section] };
    });

    let errors = {
        ...mapValues(validate(comp, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...listingValidation.validateListingBrokersCompany(get(comp, "listing.listingBrokers")),
        ...listingValidationNonRequired.validateMixedUses(get(comp, "listing.attributes.mixedUses")),
        ...listingValidationNonRequired.validatePortfolioProperties(
            get(comp, "listing.attributes.portfolioProperties")
        ),
        ...listingValidationNonRequired.validateCondos(get(comp, "listing.attributes.condos")),
    };

    return errors;
};

const validateOutsideOrAppraisalByField = (section, id, value, comp) => {
    const sectionLevelCompSections = [
        "listingInformation",
        "listingFinancial",
        "montlyRentCalculationConstraints",
        "annualRentCalculationConstraints",
        "totalConsideration",
    ];
    if (sectionLevelCompSections.includes(section)) {
        const sectionConstraints = outsideOrAppraisalCompConstraintsNotRequired[section];
        const sectionErrors = validate(comp, sectionConstraints, { fullMessages: false }) || {};
        return Object.keys(sectionConstraints).map((fullKey) => ({
            fullKey,
            resolved: isEmpty(sectionErrors[fullKey]),
        }));
    }
    const { fullKey, result } = validateFieldWithKey(outsideOrAppraisalCompConstraintsNotRequired, id, value, section);
    return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
};

export default {
    validateNonRequired,
    validateOutsideOrAppraisalByField,
};
